import { Component, Input, OnChanges } from '@angular/core';
import {
  IBillingAccount,
  IBillingDocument,
  IBillingTransactions,
  IFinancialTransaction,
  ISliceDetails,
  PaymentStatus,
  CardConfiguration,
  transactionService,
  promiseOfPaymentService,
  coverService,
  paymentInfoDisplayService,
  PaymentStatusDisplay,
  DirectDebitInfo,
} from '@dch/billing-business-logic';
import { Store } from '@ngrx/store';
import { ApplicationState } from 'src/app/state/reducers';
import { selectCustomerAccount } from 'src/app/state/selectors/customer-account.selector';
import { filter, tap } from 'rxjs/operators';
import { OnDestroyHookDirective } from '../../common/hooks';
import { TranslationService } from '@dch/ng-common';
import {
  InvoicesListByDate,
  IBillingDocumentSliceDetails,
} from '../../models/invoices-list-balance.model';
import { hasAtLeastOneBillingDocumentPaymentStatus } from '../../services/billing-show-pay-button.service';
import { MessageBoxTypesEnum } from '@dch/pxs-angular-design-system/src/lib/messages/message-box';

@Component({
  selector: 'my-bill-balance-card',
  templateUrl: './my-bill-balance-card.component.html',
  standalone: false,
})
export class MyBillBalanceCardComponent
  extends OnDestroyHookDirective
  implements OnChanges
{
  @Input() billingAccount: IBillingAccount;
  @Input() billingTransactions: IBillingTransactions;
  @Input() hasInstalmentPlan: boolean;

  amount: string;
  amountAsNumber: number;
  amountAsAbsoluteValue: string;
  billingDocuments: Array<IBillingDocument>;
  billingDocumentsWithSliceDetails: Array<IBillingDocumentSliceDetails>;
  instalmentPlanSliceDetails?: Array<ISliceDetails>;
  hasSliceDetailsLinkedToBillingDocument = false;
  paymentStatus?: PaymentStatus;
  invoiceDueDate?: string;
  singleDocumentPaymentText: string;
  singleDocumentPaymentTextColor = 'rs-txt-primary';
  singleDocumentPaymentTextHasIcon = false;
  openStatusInvoiceList?: Array<IBillingDocumentSliceDetails>;
  openStatusInvoiceListByDueDate?: InvoicesListByDate;
  overdueStatusInvoiceList?: Array<IBillingDocumentSliceDetails>;
  overdueStatusInvoiceListByMonth?: InvoicesListByDate;
  ongoingStatusInvoiceList?: Array<IBillingDocumentSliceDetails>;
  ongoingStatusInvoiceListByMonth?: InvoicesListByDate;
  unassignedFinancialTransactionsList = [] as Array<IFinancialTransaction>;
  sliceDetailsNumber?: number;
  paymentStatusDisplay: PaymentStatusDisplay;
  messageBoxTypeInfo = MessageBoxTypesEnum.Information;
  inYourFavorDescription =
    'bills.redesign.overview.balanceCard.inYourFavor.description';

  readonly selectedCustomerAccount$ = this.store.select(selectCustomerAccount);

  constructor(
    private translationService: TranslationService,
    private store: Store<ApplicationState>
  ) {
    super();
  }
  showPromiseOfPayment = false;

  ngOnChanges(): void {
    this.amount = this.billingAccount.balance;
    this.amountAsNumber = Number(this.amount);
    if (
      this.hasInstalmentPlan &&
      this.billingTransactions.instalmentPlanInformation?.amount &&
      this.amountAsNumber > 0
    ) {
      this.amount = (
        this.amountAsNumber -
        Number(this.billingTransactions.instalmentPlanInformation?.amount)
      ).toString();
    }

    this.amountAsAbsoluteValue = Math.abs(this.amountAsNumber).toString();
    this.billingDocuments =
      this.billingTransactions.openTransactions.billingDocuments;
    this.instalmentPlanSliceDetails =
      this.billingTransactions.instalmentPlanInformation?.sliceDetails;
    this.sliceDetailsNumber = this.instalmentPlanSliceDetails?.length ?? 0;
    this.billingDocumentsWithSliceDetails =
      this.addSliceDetailLinkedToInvoiceByDueDate();
    this.setUnassignedFinancialTransactionsList();

    if (
      this.billingDocuments.length === 1 &&
      this.unassignedFinancialTransactionsList.length === 0
    ) {
      this.setContentForViewAOneInvoice();
    } else {
      this.setContentForViewBInvoicesByPaymentStatusAndSlices();
    }

    this.showPromiseOfPayment = promiseOfPaymentService.shouldDoPopCall(
      this.billingAccount.overdueBalance,
      this.billingDocuments
    );
    this.getTranslationKeyByCustomerAccountSegment();
  }

  getPaymentStatusText(billingDocument: IBillingDocument) {
    const paymentStatus =
      paymentInfoDisplayService.getBillingDocumentPaymentStatus(
        (key) => this.translationService.getTranslatedValueFor(key),
        billingDocument,
        this.amount
      );
    if (billingDocument.paymentStatus === PaymentStatus.ONGOING_PAYMENT) {
      return {
        hasIcon: true,
        textColor: 'rs-status rs-success',
        paymentText: paymentStatus.paymentText,
      };
    } else if ('ERROR' === paymentStatus.paymentTextColour) {
      return {
        hasIcon: true,
        textColor: 'rs-status rs-error',
        paymentText: paymentStatus.paymentText,
      };
    } else if ('WARNING' === paymentStatus.paymentTextColour) {
      return {
        hasIcon: true,
        textColor: 'rs-status rs-status-warning',
        paymentText: paymentStatus.paymentText,
      };
    }
    return {
      hasIcon: false,
      textColor: 'rs-txt-primary',
      paymentText: paymentStatus.paymentText,
    };
  }

  objectKeys(value: any, args?: any): any {
    return Object.keys(value);
  }

  shouldShowOngoingMessage() {
    if (this.billingDocuments?.length > 1) {
      const hasAtLeastOneOpenPayementStatus =
        hasAtLeastOneBillingDocumentPaymentStatus(
          this.billingDocuments,
          PaymentStatus.OPEN
        );
      const hasAtLeastOneOngoingPayementStatus =
        hasAtLeastOneBillingDocumentPaymentStatus(
          this.billingDocuments,
          PaymentStatus.ONGOING_PAYMENT
        );
      const hasAtLeastOneOverduePayementStatus =
        hasAtLeastOneBillingDocumentPaymentStatus(
          this.billingDocuments,
          PaymentStatus.OVERDUE
        );
      const hasAtLeastOneDirectDebitInfoOngoing = this.billingDocuments.some(
        (billingDocument) =>
          billingDocument.paymentInfo?.directDebitInfo ===
          DirectDebitInfo.ONGOING
      );
      return (
        (hasAtLeastOneOngoingPayementStatus ||
          hasAtLeastOneDirectDebitInfoOngoing) &&
        (hasAtLeastOneOpenPayementStatus || hasAtLeastOneOverduePayementStatus)
      );
    }
    return false;
  }

  private setContentForViewAOneInvoice() {
    this.paymentStatus = this.billingDocuments[0].paymentStatus;
    this.invoiceDueDate = this.billingDocuments[0].dueDate;

    const { hasIcon, textColor, paymentText } = this.getPaymentStatusText(
      this.billingDocuments[0]
    );

    this.singleDocumentPaymentText = paymentText ?? '';
    this.singleDocumentPaymentTextHasIcon = hasIcon;
    this.singleDocumentPaymentTextColor = textColor;

    this.hasSliceDetailsLinkedToBillingDocument = Boolean(
      this.billingDocumentsWithSliceDetails[0].sliceDetail
    );
    if (this.hasSliceDetailsLinkedToBillingDocument) {
      this.setContentForViewBInvoicesByPaymentStatusAndSlices();
    }
  }

  private setContentForViewBInvoicesByPaymentStatusAndSlices() {
    this.openStatusInvoiceList = this.billingDocumentsWithSliceDetails.filter(
      (billingDocument) => billingDocument.paymentStatus === PaymentStatus.OPEN
    );
    if (this.openStatusInvoiceList.length > 0) {
      this.openStatusInvoiceList = this.sortInvoicesByDueDateLatestFirst(
        this.openStatusInvoiceList
      );
      this.openStatusInvoiceListByDueDate =
        this.groupInvoiceListByDocumentDateOrMonth(
          this.openStatusInvoiceList,
          PaymentStatus.OPEN
        );
    }

    this.overdueStatusInvoiceList =
      this.billingDocumentsWithSliceDetails.filter(
        (billingDocument) =>
          billingDocument.paymentStatus === PaymentStatus.OVERDUE
      );
    if (this.overdueStatusInvoiceList.length > 0) {
      this.overdueStatusInvoiceList = this.sortInvoicesByDueDateLatestFirst(
        this.overdueStatusInvoiceList
      );
      this.overdueStatusInvoiceListByMonth =
        this.groupInvoiceListByDocumentDateOrMonth(
          this.overdueStatusInvoiceList,
          PaymentStatus.OVERDUE
        );
    }

    this.ongoingStatusInvoiceList =
      this.billingDocumentsWithSliceDetails.filter(
        (billingDocument) =>
          billingDocument.paymentStatus === PaymentStatus.ONGOING_PAYMENT
      );
    if (this.ongoingStatusInvoiceList.length > 0) {
      this.ongoingStatusInvoiceList = this.sortInvoicesByDueDateLatestFirst(
        this.ongoingStatusInvoiceList
      );
      this.ongoingStatusInvoiceListByMonth =
        this.groupInvoiceListByDocumentDateOrMonth(
          this.ongoingStatusInvoiceList,
          PaymentStatus.ONGOING_PAYMENT
        );
    }
  }

  private setUnassignedFinancialTransactionsList() {
    if (
      this.billingTransactions.openTransactions.unassignedFinancialTransactions
        .length > 0
    ) {
      this.unassignedFinancialTransactionsList =
        this.billingTransactions.openTransactions.unassignedFinancialTransactions.filter(
          (unassignedFinancialTransaction) =>
            Number(unassignedFinancialTransaction.amount) !== 0
        );
    }
  }

  private addSliceDetailLinkedToInvoiceByDueDate() {
    return this.billingDocuments.map((invoice) => {
      const sliceDetailLinkToInvoiceByDueDate =
        this.instalmentPlanSliceDetails?.filter(
          (sliceDetail) => sliceDetail.dueDate === invoice.dueDate
        );
      return {
        ...invoice,
        sliceDetail:
          sliceDetailLinkToInvoiceByDueDate?.length > 0
            ? sliceDetailLinkToInvoiceByDueDate
            : undefined,
      };
    });
  }

  private groupInvoiceListByDocumentDateOrMonth(
    invoiceList: IBillingDocument[],
    paymentStatus: PaymentStatus
  ) {
    return invoiceList.reduce((result, invoice) => {
      const transactionConfiguration: CardConfiguration =
        this.getTransactionConfigurationByBillingDocument(invoice);
      const documentDate =
        paymentStatus === PaymentStatus.OPEN
          ? invoice.documentDate
          : this.getDueDateMonthYearTranslation(invoice.documentDate);
      const configuredInvoice = {
        ...invoice,
        invoiceTypeTranslation: transactionConfiguration.subtitle,
      };
      if (!result[documentDate]) {
        result[documentDate] = {
          invoices: [],
          monthYear: this.getDueDateMonthYearTranslation(invoice.documentDate),
          ...this.getPaymentStatusText(invoice),
        };
      }

      result[documentDate].invoices.push(configuredInvoice);
      return result;
    }, {});
  }

  private sortInvoicesByDueDateLatestFirst(invoicesList: IBillingDocument[]) {
    return invoicesList.sort(
      (a, b) => new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()
    );
  }

  private getTransactionConfigurationByBillingDocument(
    billingDocument: IBillingDocument
  ) {
    return transactionService.configureCard(billingDocument, (key) =>
      this.translationService.getTranslatedValueFor(key)
    );
  }

  private getDueDateMonthYearTranslation(invoiceDueDate: string) {
    const date = invoiceDueDate.split('/');
    const month = coverService.getMonthTranslationsByMonthNumber(
      Number(date[1]) - 1,
      (key: string) => this.translationService.getTranslatedValueFor(key)
    );
    return `${month} ${date[2]}`;
  }

  private getTranslationKeyByCustomerAccountSegment() {
    this.addSubscription(
      this.selectedCustomerAccount$
        .pipe(
          filter((customerAccount) => customerAccount !== undefined),
          tap((customerAccount) => {
            const resKey =
              customerAccount.getSelectedCustomer().subsegment === 'RES'
                ? 'RES.'
                : '';
            this.inYourFavorDescription = `bills.redesign.overview.balanceCard.inYourFavor.${resKey}description`;
          })
        )
        .subscribe()
    );
  }
}
